import { CSSProperties, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Flag } from 'lucide-react';
import { cn } from '@bliks-io/ui';
import { useOrganization } from '@/api/queries/useOrganization';
import {
  WIZARD_STEPS,
  type WizardProgress,
  type WizardStepDef,
  type WizardStepId
} from '../wizardSteps';

interface WizardRailProps {
  currentStep: WizardStepId;
  progress: WizardProgress;
  skippedSteps: Set<WizardStepId>;
  onStepClick: (stepId: WizardStepId) => void;
}

interface RailEntryProps extends WizardRailProps {
  step: WizardStepDef;
  isFirst: boolean;
  isLast: boolean;
  /** The spine segment above this entry lies before/at the active step */
  topSegmentReached: boolean;
  /** The spine segment below this entry lies before the active step */
  bottomSegmentReached: boolean;
}

const SPINE_LEFT = 'left-[14px]';

// Half the node diameter plus a little breathing room, so the spine stops
// short of the circle instead of touching it: 28px steps → 14px, the 44px
// overview flag → 22px.
const STEP_NODE_GAP = '14px';
const OVERVIEW_NODE_GAP = '22px';

function SpineSegment({ position, reached }: { position: 'top' | 'bottom'; reached: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        'absolute w-0.5 -translate-x-1/2',
        SPINE_LEFT,
        position === 'top'
          ? 'top-0 h-[calc(50%-var(--node-gap))]'
          : 'bottom-0 h-[calc(50%-var(--node-gap))]',
        reached ? 'bg-bliks-green-500' : 'bg-surface-strong'
      )}
    />
  );
}

/** The uninterrupted spine run beside a sub-step entry or the pre-overview filler. */
function FullHeightSpine({ reached }: { reached: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        'absolute inset-y-0 w-0.5 -translate-x-1/2',
        SPINE_LEFT,
        reached ? 'bg-bliks-green-500' : 'bg-surface-strong'
      )}
    />
  );
}

function RailIndicator({
  step,
  isDone,
  isCurrent,
  isSkipped
}: {
  step: WizardStepDef;
  isDone: boolean;
  isCurrent: boolean;
  isSkipped: boolean;
}) {
  const isOverview = step.railNumber === undefined;

  if (isOverview) {
    return (
      <span
        className={cn(
          'relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full',
          isCurrent || isDone ? 'bg-bliks-green-500 text-foreground' : 'bg-muted text-fg-subtle'
        )}
      >
        <Flag className="h-[17px] w-[17px]" strokeWidth={1.8} />
      </span>
    );
  }
  if (isDone && !isCurrent) {
    return (
      <span className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-bliks-green-500 text-foreground">
        <Check className="h-[14px] w-[14px]" strokeWidth={3} />
      </span>
    );
  }
  if (isCurrent) {
    return (
      <span className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-bliks-green-500 text-xs font-bold text-foreground ring-4 ring-bliks-green-500/20">
        {step.railNumber}
      </span>
    );
  }
  // Skipped (acknowledged, not imported): a muted check, distinct from the green
  // "done" and the outlined "to do".
  if (isSkipped) {
    return (
      <span className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-surface-strong text-muted-foreground">
        <Check className="h-[14px] w-[14px]" strokeWidth={2.5} />
      </span>
    );
  }
  return (
    <span className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-[1.5px] border-line-strong bg-white text-xs font-semibold text-fg-subtle">
      {step.railNumber}
    </span>
  );
}

function RailLabel({
  step,
  isCurrent,
  isReachable,
  titleClassName
}: {
  step: WizardStepDef;
  isCurrent: boolean;
  isReachable: boolean;
  titleClassName: string;
}) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        'min-w-0 rounded-[9px] transition-colors',
        isCurrent && 'bg-bliks-green-500/10 px-2.75 py-1.75',
        !isCurrent && isReachable && 'hover:bg-surface-subtle'
      )}
    >
      <span className={cn('block', titleClassName)}>{step.labelKey && t(step.labelKey)}</span>
      {step.descriptionKey && (
        <span
          className={cn(
            'block text-[11.5px] leading-[1.35]',
            isCurrent ? 'text-fg-secondary' : 'text-fg-subtle'
          )}
        >
          {t(step.descriptionKey)}
        </span>
      )}
    </span>
  );
}

function RailEntry({
  step,
  currentStep,
  progress,
  skippedSteps,
  onStepClick,
  isFirst,
  isLast,
  topSegmentReached,
  bottomSegmentReached
}: RailEntryProps) {
  const isCurrent = currentStep === step.id;
  const isDone = step.isDone?.(progress) ?? false;
  const isSkipped = skippedSteps.has(step.id) && !isDone && !isCurrent;
  const isReachable = step.isReachable(progress);
  const isSubStep = !!step.subStepOf;

  if (isSubStep) {
    return (
      <div className="relative">
        <FullHeightSpine reached={topSegmentReached} />
        <button
          onClick={() => isReachable && onStepClick(step.id)}
          disabled={!isReachable}
          className={cn(
            'flex w-full items-center gap-3 py-1 pl-10 text-left',
            !isReachable && 'cursor-not-allowed'
          )}
        >
          <span
            className={cn(
              'h-3 w-3 shrink-0 rounded-full',
              isDone || isCurrent ? 'bg-bliks-green-500' : 'bg-surface-strong',
              isCurrent && !isDone && 'ring-4 ring-bliks-green-500/20'
            )}
          />
          <RailLabel
            step={step}
            isCurrent={isCurrent}
            isReachable={isReachable}
            titleClassName="text-[13px] font-semibold text-foreground"
          />
        </button>
      </div>
    );
  }

  const nodeGap = step.railNumber === undefined ? OVERVIEW_NODE_GAP : STEP_NODE_GAP;

  return (
    <div className="relative" style={{ '--node-gap': nodeGap } as CSSProperties}>
      {!isFirst && <SpineSegment position="top" reached={topSegmentReached} />}
      {!isLast && <SpineSegment position="bottom" reached={bottomSegmentReached} />}
      <button
        onClick={() => isReachable && onStepClick(step.id)}
        disabled={!isReachable}
        className={cn(
          'flex w-full items-center gap-3 py-1.5 text-left',
          !isReachable && 'cursor-not-allowed'
        )}
      >
        <span className="flex w-7 shrink-0 justify-center">
          <RailIndicator step={step} isDone={isDone} isCurrent={isCurrent} isSkipped={isSkipped} />
        </span>
        <RailLabel
          step={step}
          isCurrent={isCurrent}
          isReachable={isReachable}
          titleClassName={cn(
            'text-sm',
            (isCurrent || isDone) && 'font-semibold text-foreground',
            !isCurrent && !isDone && (isReachable ? 'text-fg-secondary' : 'text-fg-subtle')
          )}
        />
      </button>
    </div>
  );
}

/**
 * Left spine of the wizard: a continuous timeline threading numbered steps
 * 1-6, indented sub-steps (Manager auflösen under Mitarbeiter), and the
 * bottom-pinned flag entry for Überblick & Abschluss. Welcome/outcomes have
 * no entry of their own.
 */
export function WizardRail({ currentStep, progress, skippedSteps, onStepClick }: WizardRailProps) {
  const { t } = useTranslation();
  const { data: org } = useOrganization();
  const railSteps = WIZARD_STEPS.filter(
    (step) =>
      (step.railNumber !== undefined || step.subStepOf || step.id === 'overview') &&
      (step.isVisible?.(progress) ?? true)
  );
  const currentIndex = railSteps.findIndex((step) => step.id === currentStep);
  const overviewIndex = railSteps.length - 1;

  return (
    <div className="flex h-full w-[300px] shrink-0 flex-col border-r border-line bg-white px-6 pb-7 pt-7.5">
      <div className="flex items-center gap-3">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-bliks-green-500/25 bg-bliks-green-500/10">
          <img
            src="/assets/images/logo.svg"
            className="h-[25px] w-[25px]"
            alt={t('initialSetup.topBar.logoAlt')}
          />
        </span>
        <div>
          <div className="font-display text-[17px] font-bold text-foreground">
            {t('initialSetup.title')}
          </div>
          <div className="text-xs text-fg-secondary">{org?.name ?? ''}</div>
        </div>
      </div>

      {/* overflow-y-auto also clips horizontally: the 44px flag overflows its
          28px indicator column by 8px per side, so trade margin for padding to
          keep that overflow (and the current step's ring) inside the scrollport. */}
      <nav className="-mx-2 mt-7 flex flex-1 flex-col overflow-y-auto px-2">
        {railSteps.map((step, index) => (
          <Fragment key={step.id}>
            {index === overviewIndex && (
              <div className="relative min-h-4 flex-1">
                <FullHeightSpine reached={index <= currentIndex} />
              </div>
            )}
            <RailEntry
              step={step}
              currentStep={currentStep}
              progress={progress}
              skippedSteps={skippedSteps}
              onStepClick={onStepClick}
              isFirst={index === 0}
              isLast={index === railSteps.length - 1}
              topSegmentReached={index <= currentIndex}
              bottomSegmentReached={index < currentIndex}
            />
          </Fragment>
        ))}
      </nav>
    </div>
  );
}
